import React, { useState, useEffect } from 'react';
import { Container, Button, Modal, Form, Row, Col, Badge } from 'react-bootstrap';
import { BsPlusLg, BsPencil, BsTrash } from 'react-icons/bs';
import { toast } from 'react-toastify';
import workflowService from '../services/workflowService';
import ResponsiveTable from './ResponsiveTable';

const ProcessTypes = () => {
    const [processTypes, setProcessTypes] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedProcessType, setSelectedProcessType] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
    });

    useEffect(() => {
        loadProcessTypes();
    }, []);

    const loadProcessTypes = async () => {
        try {
            const data = await workflowService.getAllProcessTypes();
            setProcessTypes(data);
        } catch (error) {
            console.error('Error loading process types:', error);
            toast.error('Failed to load process types');
        }
    };

    const handleShowModal = (processType = null) => {
        if (processType) {
            setFormData({
                name: processType.name,
                description: processType.description,
            });
            setSelectedProcessType(processType);
        } else {
            setFormData({ 
                name: '', 
                description: '', 
            });
            setSelectedProcessType(null);
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedProcessType(null);
        setFormData({ 
            name: '', 
            description: '', 
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        const errors = [];
        
        if (!formData.name || formData.name.trim() === '') {
            errors.push('Process Type name is required');
        }
        
        if (errors.length > 0) {
            toast.error(errors.join('\n'));
            return;
        }
        
        try {
            if (selectedProcessType) {
                await workflowService.updateProcessType(selectedProcessType.id, formData);
                toast.success('Process type updated successfully');
            } else {
                await workflowService.createProcessType(formData);
                toast.success('Process type created successfully');
            }
            handleCloseModal();
            loadProcessTypes();
        } catch (error) {
            console.error('Error saving process type:', error);
            toast.error('Failed to save process type');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this process type?')) {
            try {
                await workflowService.deleteProcessType(id);
                toast.success('Process type deleted successfully');
                loadProcessTypes();
            } catch (error) {
                console.error('Error deleting process type:', error);
                toast.error('Failed to delete process type');
            }
        }
    };

    // Define columns for ResponsiveTable
    const columns = [
        {
            key: 'name',
            label: 'Name'
        },
        {
            key: 'description',
            label: 'Description'
        },
        {
            key: 'isActive',
            label: 'Status',
            render: (value) => (
                <Badge bg={value ? 'success' : 'secondary'}>
                    {value ? 'Active' : 'Inactive'}
                </Badge>
            )
        },
        {
            key: 'createdAt',
            label: 'Created At',
            render: (value) => new Date(value).toLocaleDateString()
        }
    ];

    // Define actions for ResponsiveTable
    const actions = [
        {
            icon: <BsPencil />,
            variant: 'outline-primary',
            onClick: (processType) => handleShowModal(processType)
        },
        {
            icon: <BsTrash />,
            variant: 'outline-danger',
            onClick: (processType) => handleDelete(processType.id)
        }
    ];

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex gap-2">
                    <Button variant="primary" onClick={() => handleShowModal()}>
                        <BsPlusLg /> Add Process Type
                    </Button>
                </div>
            </div>

            <ResponsiveTable 
                data={processTypes}
                columns={columns}
                actions={actions}
            />

            <Modal show={showModal} onHide={handleCloseModal}>
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {selectedProcessType ? 'Edit Process Type' : 'New Process Type'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>

                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit">
                            {selectedProcessType ? 'Update' : 'Create'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default ProcessTypes; 